import type { Solution } from '../types';

// Extend Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer?: any[];
  }
}

/**
 * Extract solution metadata with fallback values
 */
const getSolutionMetadata = (solution: Solution) => {
  const s = solution as any;
  
  const solutionProvider = solution.builtBy || 
                          s.providerName || 
                          s.provider || 
                          s.builder ||
                          s.company ||
                          'Ellucian Partner';
                          
  const entitlementType = solution.licence || 
                         s.entitlementType || 
                         s.entitlement ||
                         s.licenseType ||
                         'Standard License';
                         
  const solutionBuilder = solution.builtBy || 
                         s.builderName || 
                         s.builder ||
                         s.developer ||
                         solutionProvider ||
                         'Third Party';
                         
  const itemId = solution.id?.toString() || 
                solution.storefrontIntegrationId || 
                s.solutionId ||
                `solution_${Date.now()}`;
                
  const itemName = solution.name || 
                  s.title || 
                  s.solutionName ||
                  'Marketplace Solution';

  return {
    solutionProvider,
    entitlementType,
    solutionBuilder,
    itemId,
    itemName
  };
};

/**
 * Base function to track any custom event with consent management
 * Only tracks events if user has granted appropriate consent
 */
export const trackEvent = (eventName: string, parameters: any = {}) => {
  // Check analytics consent before tracking
  const hasConsent = hasAnalyticsConsent();
  const isProduction = process.env.NODE_ENV === 'production';
  
  // Block tracking if user declined analytics consent in production
  if (isProduction && !hasConsent) {
    return;
  }
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    const eventData = {
      event: eventName,
      debug_mode: false,
      ...parameters
    };
    
    // Add consent context to all events except consent_choice_completed
    if (eventName !== 'consent_choice_completed') {
      eventData.consent_granted = hasAnalyticsConsent();
      eventData.marketing_consent = hasMarketingConsent();
    }
    
    try {
      window.dataLayer.push(eventData);
    } catch (error) {
      console.error('Analytics error:', error);
    }
  }
};

/**
 * Track when a user views a solution detail page
 * Usage: trackSolutionView(solution)
 */
export const trackSolutionView = (solution: Solution) => {
  if (!solution || !solution.name) {
    return;
  }
  
  const { solutionProvider, entitlementType, solutionBuilder, itemId, itemName } = getSolutionMetadata(solution);

  trackEvent('view_item', {
    currency: 'USD',
    value: 0,
    // Event-level parameters for GA4 custom dimensions
    item_name: itemName,           // Primary metric: Solution name (custom dimension)
    item_id: itemId,              // Secondary: Solution ID
    item_category: solution.type?.[0] || 'Solutions',
    solution_provider: solutionProvider,
    entitlement_type: entitlementType,
    // Enhanced Ecommerce structure
    items: [{
      item_id: itemId,
      item_name: itemName,
      item_category: solution.type?.[0] || 'Solutions',
      item_category2: entitlementType,
      price: 0,
      quantity: 1
    }],
    // Business intelligence metadata  
    is_entitled: solution.isEntitled || false,
    solution_builder: solutionBuilder,
    integrated_products: solution.integratedEllucianSolutions?.join(',') || '',
    page_type: 'solution_detail'
  });
};

/**
 * Track solution popularity with enhanced context
 * Usage: trackSolutionPopularity(solution, 'search_results', 'homepage_featured')
 */
export const trackSolutionPopularity = (solution: Solution, context: string, source: string = 'direct') => {
  const { solutionProvider, entitlementType, itemId, itemName } = getSolutionMetadata(solution);
  
  trackEvent('solution_interaction', {
    interaction_type: 'view',
    item_name: itemName,
    item_id: itemId,
    item_category: solution.type?.[0] || 'Solutions',
    context: context, // 'search_results', 'category_browse', 'featured_list'
    source: source,   // 'homepage', 'search', 'category_page'
    solution_provider: solutionProvider,
    entitlement_type: entitlementType,
    page_type: 'solution_discovery'
  });
};

/**
 * Track when a user performs a search
 * Uses GA4 recommended e-commerce schema
 * Usage: trackSearch('student portal')
 */
export const trackSearch = (searchTerm: string) => {
  
  trackEvent('search', {
    search_term: searchTerm,
    item_name: `Search: ${searchTerm}`,
    item_id: `search_${Date.now()}`,
    item_category: 'Search'
  });
};

/**
 * Track button clicks throughout the app
 * Usage: trackButtonClick('download_solution', 'solution_detail_page')
 */
export const trackButtonClick = (buttonName: string, location: string) => {
  trackEvent('button_click', {
    button_name: buttonName,
    click_location: location,
    item_name: `Button: ${buttonName}`,
    item_id: `button_${buttonName.toLowerCase().replace(/\s+/g, '_')}`,
    item_category: 'UI_Interaction',
    event_category: 'engagement',
    event_label: 'ui_interaction'
  });
};

/**
 * Track when users view a list of solutions (search results, category page, etc.)
 * Enhanced with rich metadata analysis
 * Usage: trackSolutionListView(solutions, 'search_results', 'homepage')
 */
export const trackSolutionListView = (solutions: Solution[], context: string = 'browse', source: string = 'homepage') => {
  // Filter out any solutions without proper data
  const validSolutions = solutions.filter(solution => 
    solution && (solution.id || solution.storefrontIntegrationId) && solution.name
  );
  
  if (validSolutions.length === 0) {
    console.warn('No valid solutions to track');
    return;
  }

  // Analyze the solutions in the list
  const entitledCount = validSolutions.filter(s => s.isEntitled).length;
  const ellucianCount = validSolutions.filter(s => s.builtBy === 'Ellucian').length;
  const partnerCount = validSolutions.filter(s => s.builtBy === 'Partner').length;
  const integrationCount = validSolutions.filter(s => s.type?.includes('Integration')).length;
  
  trackEvent('view_item_list', {
    item_list_name: context,
    item_list_source: source,
    item_name: `${context} List (${validSolutions.length} items)`,
    item_id: `list_${context.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`,
    item_category: 'Solution_List',
    items: validSolutions.map((solution, index) => ({
      item_id: solution.id?.toString() || solution.storefrontIntegrationId || 'unknown',
      item_name: solution.name || 'Unknown Solution',
      item_category: solution.type?.[0] || 'Solutions',
      item_category2: solution.licence || 'Standard',
      item_list_position: index + 1,
      price: 0,
      quantity: 1
    })),
    // List analysis metrics
    total_solutions: validSolutions.length,
    entitled_solutions: entitledCount,
    ellucian_solutions: ellucianCount,
    partner_solutions: partnerCount,
    integration_solutions: integrationCount,
    entitlement_mix: `${entitledCount}entitled_${validSolutions.length - entitledCount}standard`,
    builder_mix: `${ellucianCount}ellucian_${partnerCount}partner`
  });
};

/**
 * Track when users request solution installation (conversion event)
 * Usage: trackSolutionInstallRequest(solution)
 */
export const trackSolutionInstallRequest = (solution: Solution) => {
  const { solutionProvider, entitlementType, solutionBuilder, itemId, itemName } = getSolutionMetadata(solution);
  
  // Track as request_install event (will appear in Events section)
  trackEvent('request_install', {
    transaction_id: `install-${itemId}-${Date.now()}`,
    solution_provider: solutionProvider,
    entitlement_type: entitlementType,
    solution_builder: solutionBuilder,
    is_entitled: solution.isEntitled || false,
    item_name: itemName,
    item_category: solution.type?.[0] || 'Solutions',
    item_id: itemId
  });
};

/**
 * Track when users request more info about a solution (lead generation)
 * Uses your actual Solution interface data
 * Usage: trackSolutionInfoRequest(solution, 'contact_form', 'University Name')
 */
export const trackSolutionInfoRequest = (solution: Solution, method: string = 'contact_form', institutionName?: string) => {
  const { solutionProvider, entitlementType, solutionBuilder, itemId, itemName } = getSolutionMetadata(solution);
                         
  const leadSource = method || 'contact_form';
  trackEvent('generate_lead', {
    currency: 'USD',
    value: 0,
    item_name: itemName,
    item_id: itemId,
    item_category: solution.type?.[0] || 'Solutions',
    solution_provider: solutionProvider,
    entitlement_type: entitlementType,
    solution_builder: solutionBuilder,
    lead_source: leadSource,
    institution_name: institutionName,
    conversion_type: 'info_request',
    items: [{
      item_id: itemId,
      item_name: itemName,
      item_category: solution.type?.[0] || 'Solutions',
      item_category2: entitlementType,
      price: 0,
      quantity: 1
    }]
  });
};

/**
 * Track solution filtering behavior - understand user preferences
 * Usage: trackSolutionFiltering(['Integration'], ['Banner'], ['Ellucian'])
 */
export const trackSolutionFiltering = (
  selectedTypes: string[] = [],
  selectedProducts: string[] = [], 
  selectedBuilders: string[] = [],
  selectedEntitlements: string[] = [],
  resultCount: number = 0
) => {
  trackEvent('filter_solutions', {
    selected_types: selectedTypes.join(','),
    selected_products: selectedProducts.join(','),
    selected_builders: selectedBuilders.join(','),
    selected_entitlements: selectedEntitlements.join(','),
    result_count: resultCount,
    item_name: `Filter: ${selectedTypes.join(',') || 'All'} (${resultCount} results)`,
    item_id: `filter_${Date.now()}`,
    item_category: 'Filter',
    filter_combination: `${selectedTypes.length}types_${selectedProducts.length}products_${selectedBuilders.length}builders`,
    has_entitlement_filter: selectedEntitlements.length > 0
  });
};

/**
 * Track entitlement group selections - answers "What group of listings based on entitlement are customers selecting?"
 * Usage: trackEntitlementGroupSelection(solutions, 'Standard')
 */
export const trackEntitlementGroupSelection = (solutions: Solution[], selectedEntitlement: string) => {
  const entitlementSolutions = solutions.filter(s => s.licence === selectedEntitlement);
  const builderBreakdown = entitlementSolutions.reduce((acc, solution) => {
    const builder = solution.builtBy || 'Unknown';
    acc[builder] = (acc[builder] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const typeBreakdown = entitlementSolutions.reduce((acc, solution) => {
    const type = solution.type?.[0] || 'Unknown';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  trackEvent('entitlement_group_selection', {
    selected_entitlement: selectedEntitlement,
    solution_count: entitlementSolutions.length,
    item_name: `${selectedEntitlement} Entitlement Group (${entitlementSolutions.length} solutions)`,
    item_id: `entitlement_${selectedEntitlement.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`,
    item_category: 'Entitlement_Group',
    builder_breakdown: Object.entries(builderBreakdown).map(([builder, count]) => `${builder}:${count}`).join(','),
    type_breakdown: Object.entries(typeBreakdown).map(([type, count]) => `${type}:${count}`).join(','),
    ellucian_count: builderBreakdown['Ellucian'] || 0,
    partner_count: builderBreakdown['Partner'] || 0,
    event_category: 'entitlement_analysis'
  });
};

/**
 * Track solution comparison behavior - which solutions users compare
 * Usage: trackSolutionComparison([solution1, solution2], 'side_by_side')
 */
export const trackSolutionComparison = (solutions: Solution[], comparisonType: string = 'side_by_side') => {
  const builderSet = new Set(solutions.map(s => s.builtBy).filter(Boolean));
  const typeSet = new Set(solutions.flatMap(s => s.type || []));
  
  trackEvent('compare_solutions', {
    comparison_type: comparisonType,
    solution_count: solutions.length,
    item_name: `Compare ${solutions.length} Solutions`,
    item_id: `comparison_${Date.now()}`,
    item_category: 'Solution_Comparison',
    items: solutions.map((solution, index) => ({
      item_id: solution.id?.toString() || solution.storefrontIntegrationId || 'unknown',
      item_name: solution.name || 'Unknown Solution',
      item_category: solution.type?.[0] || 'Solutions',
      item_category2: solution.licence || 'Standard',
      index: index
    })),
    // Comparison insights
    comparing_entitled: solutions.some(s => s.isEntitled),
    comparing_builders: Array.from(builderSet).join(','),
    comparing_types: Array.from(typeSet).join(','),
    mixed_entitlements: new Set(solutions.map(s => s.licence)).size > 1
  });
};

/**
 * Track user authentication events with enhanced context
 * Usage: trackUserLogin('okta', true, 'sso')
 */
export const trackUserLogin = (method: string, success: boolean, userType: string = 'user') => {
  trackEvent('login', {
    method: method,
    success: success,
    user_type: userType,
    item_name: `Login: ${method}`,
    item_id: `login_${method.toLowerCase()}_${Date.now()}`,
    item_category: 'Authentication'
  });
};

/**
 * Initialize consent mode with default settings
 * Call this before GTM initialization
 */
export const initializeConsentMode = () => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  
  window.dataLayer.push({
    'event': 'consent_init'
  });
};

/**
 * Update user consent preferences
 */
export const updateConsent = (
  analyticsGranted: boolean, 
  marketingGranted: boolean = false
) => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    const consentEvent = {
      event: 'consent_update',
      analytics_consent: analyticsGranted ? 'granted' : 'denied',
      marketing_consent: marketingGranted ? 'granted' : 'denied',
      analytics_storage: analyticsGranted ? 'granted' : 'denied',
      ad_storage: marketingGranted ? 'granted' : 'denied',
      ad_user_data: marketingGranted ? 'granted' : 'denied',
      ad_personalization: marketingGranted ? 'granted' : 'denied',
      personalization_storage: marketingGranted ? 'granted' : 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
      consent_method: 'marketplace',
      consent_timestamp: new Date().toISOString()
    };
    
    window.dataLayer.push(consentEvent);
  }
  
  // Store consent locally
  const consentRecord = {
    analytics: analyticsGranted,
    marketing: marketingGranted,
    functionality: true,
    personalization: analyticsGranted,
    timestamp: new Date().toISOString(),
    version: '2.0',
    method: 'marketplace',
    expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
  };

  localStorage.setItem('ellucian-marketplace-consent', JSON.stringify(consentRecord));
  
  // Track consent choice for compliance
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'consent_choice_completed',
      consent_analytics: analyticsGranted,
      consent_marketing: marketingGranted,
      consent_method: 'marketplace',
      consent_granted: analyticsGranted,
      marketing_consent: marketingGranted
    });
  }
};

/**
 * Get current consent preferences from localStorage
 * Returns null if no consent has been given
 */
export const getCurrentConsent = () => {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem('ellucian-marketplace-consent');
    if (!stored) return null;

    const consent = JSON.parse(stored);
    
    // Check if consent has expired (1 year)
    if (consent.expires && new Date(consent.expires) < new Date()) {
      localStorage.removeItem('ellucian-marketplace-consent');
      return null;
    }

    return consent;
  } catch (error) {
    console.error('Error reading consent preferences:', error);
    return null;
  }
};

/**
 * Check if user has granted specific consent type
 * Usage: if (hasAnalyticsConsent()) { trackEvent(...) }
 */
export const hasAnalyticsConsent = (): boolean => {
  const consent = getCurrentConsent();
  return consent?.analytics === true;
};

/**
 * Check if user has granted marketing consent
 */
export const hasMarketingConsent = (): boolean => {
  const consent = getCurrentConsent();
  return consent?.marketing === true;
};

/**
 * Track solution installation requests
 * Usage: trackPurchase('TXN-12345', solution)
 */
export const trackPurchase = (transactionId: string, solution: Solution) => {
  const { solutionProvider, entitlementType, solutionBuilder, itemId, itemName } = getSolutionMetadata(solution);
  
  trackEvent('request_install', {
    transaction_id: transactionId,
    solution_provider: solutionProvider,
    entitlement_type: entitlementType,
    solution_builder: solutionBuilder,
    is_entitled: solution.isEntitled || false,
    item_name: itemName,
    item_category: solution.type?.[0] || 'Solutions',
    item_id: itemId
  });
};

/**
 * Track page views manually (useful for SPA routing)
 * Usage: trackPageView('/solutions/student-portal', 'Solution Detail - Student Portal')
 */
export const trackPageView = (pagePath: string, pageTitle: string, userId?: string) => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    user_id: userId,
    item_name: pageTitle,
    item_id: `page_${pagePath.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}`,
    item_category: 'Page',
    event_category: 'navigation'
  });
};

/**
 * Track marketplace visits - answers "How many people are visiting the marketplace?"
 * Usage: trackMarketplaceVisit()
 */
export const trackMarketplaceVisit = () => {
  trackEvent('marketplace_visit', {
    page_type: 'marketplace_home',
    item_name: 'Marketplace Homepage Visit',
    item_id: `marketplace_visit_${Date.now()}`,
    item_category: 'Marketplace',
    event_category: 'engagement'
  });
};

/**
 * Track solution card clicks - answers "Which listing is the most clicked?"
 * Usage: trackSolutionClick(solution, 'search_results')
 */
export const trackSolutionClick = (solution: Solution, context: string = 'browse') => {
  const { solutionProvider, entitlementType, itemId, itemName } = getSolutionMetadata(solution);
  
  trackEvent('select_item', {
    item_list_name: context,
    item_name: itemName,
    item_id: itemId,
    item_category: solution.type?.[0] || 'Solutions',
    items: [{
      item_id: itemId,
      item_name: itemName,
      item_category: solution.type?.[0] || 'Solutions',
      item_category2: entitlementType
    }],
    // Click tracking metadata
    solution_provider: solutionProvider,
    entitlement_type: entitlementType,
    is_entitled: solution.isEntitled || false,
    click_context: context,
    event_category: 'engagement'
  });
};

/**
 * Track filtering actions
 * Usage: trackFilter('category', 'Student Services')
 */
export const trackFilter = (filterType: string, filterValue: string) => {
  trackEvent('apply_filter', {
    filter_type: filterType,
    filter_value: filterValue,
    item_name: `${filterType}: ${filterValue}`,
    item_id: `filter_${filterType.toLowerCase()}_${filterValue.toLowerCase().replace(/\s+/g, '_')}`,
    item_category: 'Filter_Action',
    event_category: 'marketplace',
    event_label: 'filter_interaction'
  });
};

